// Global Types
import type {
  $Translate,
} from 'common.language';
import type {
  $FullRegattaCourse,
  $Model,
  $ModelUpsertParams,
} from 'common.data';

// Global Enums
import {
  adminPages,
  adminSections,
} from 'common.enums';

// Global Components
import * as React from 'react';
import {
  Button,
  Card,
  Table,
} from 'reactstrap';
import {
  DeleteDialog,
  FormDialog,
  Loader,
  SectionHeader,
  TdDate,
} from '@vroskus/library-components';

// Helpers
import {
  cleanFormValues,
  isDiff,
} from '@vroskus/library-helpers/dist/browser';
import {
  formatTimeRange,
} from 'common.helpers/dist/browser';
import {
  defaultDeleteItem,
  defaultGetData,
  defaultUpsertItem,
  publicBorder,
} from '../../../helpers';

// Components
import Page from '../../layout/Page';
import EmptyTable from '../../common/EmptyTable';
import RegattaCourseFields from '../../fields/RegattaCourseFields';
import RegattaCourseRaceFields from '../../fields/RegattaCourseRaceFields';
import ThQty from '../../common/ThQty';

// Services
import Services from '../../../services';

// Types
import type {
  $ReducerState,
} from '../../../redux';
import type {
  $RedirectAction,
} from '../../../redux/modules/main/actions';

export type $OwnProps = $Translate;

export type $StateProps = {
  main: $ReducerState<'main'>;
};

export type $DispatchProps = {
  redirect: $RedirectAction;
};

type $Props = $DispatchProps & $OwnProps & $StateProps;

type $State = {
  data: Array<$FullRegattaCourse>;
  loading: boolean;
};

const pageId = adminPages.regattaCoursesPage;

class RegattaCoursesPageView extends React.Component<$Props, $State> {
  constructor(props: $Props) {
    super(props);

    this.state = {
      data: [],
      loading: false,
    };
  }

  componentDidMount(): void {
    const {
      main,
      redirect,
    } = this.props;

    if (main.activeRegattaId === null) {
      redirect({
        pathname: '/regattas',
      });
    }

    this.getData();
    this.setupSocket();
  }

  componentDidUpdate(prevProps: $Props): void {
    const {
      main,
    } = this.props;

    if (main.activeRegattaId !== null && isDiff(
      main.activeRegattaId,
      prevProps.main.activeRegattaId,
    )) {
      this.getData();
    }
  }

  setupSocket(): void {
    Services.Socket.on({
      action: (data) => {
        const {
          main,
        } = this.props;

        if (main.activeRegattaId === data.RegattaId && data.modelName === 'RegattaCourse') {
          this.getData();
        }
      },
      type: Services.Socket.type.modelChangedEvent,
    });
  }

  setStateAsync(state: Partial<$State>): Promise<void> {
    return new Promise((resolve) => {
      this.setState(
        (pervState: $State) => ({
          ...pervState,
          ...state,
        }),
        resolve,
      );
    });
  }

  async getData(): Promise<void> {
    const {
      main,
    } = this.props;

    const RegattaId = main.activeRegattaId;

    await defaultGetData({
      action: async () => {
        if (RegattaId !== null) {
          await this.setStateAsync({
            loading: true,
          });

          const data = await Services.Api.getManyRegattaCourse({
            RegattaId,
          });

          await this.setStateAsync({
            data,
            loading: false,
          });
        }
      },
      errorAction: async () => this.setStateAsync({
        loading: false,
      }),
    });
  }

  renderHeader() {
    const {
      main,
      t,
    } = this.props;

    const RegattaId = main.activeRegattaId;

    if (RegattaId !== null) {
      const newItem: $ModelUpsertParams<'RegattaCourse'> = {
        Name: '',
        RegattaId,
        ShowMap: true,
      };

      return (
        <Card id={adminSections[pageId].header}>
          <SectionHeader
            iconClass={'icon-share'}
            rightButtons={(
              <FormDialog
                fields={(
                  <RegattaCourseFields item={newItem} />
                )}
                id={'create-regatta-course-dialog'}
                labelCloseButton={t('Close')}
                labelSaveButton={t('Save')}
                onAction={(values) => defaultUpsertItem({
                  action: async () => {
                    const params = cleanFormValues<$Model<'RegattaCourse'>>({
                      ...newItem,
                      ...values,
                    });

                    await Services.Api.upsertRegattaCourse(params);
                  },
                })}
                subTitle={t('New')}
                title={t('Regatta course')}
              >
                <Button
                  color={'info'}
                  id={'create-regatta-course-button'}
                  size={'sm'}
                  title={t('Add new regatta course')}
                >
                  {t('Add new')}
                </Button>
              </FormDialog>
            )}
            title={t('Regatta courses')}
          />
        </Card>
      );
    }

    return null;
  }

  renderButtons(dataItem: $Model<'RegattaCourse'>) {
    const {
      redirect,
      t,
    } = this.props;

    const {
      id,
    } = dataItem;

    return (
      <div className={'d-flex justify-content-end'}>
        <Button
          className={'mr-1'}
          color={'warning'}
          id={`redirect-regatta-course-page-button-${id}`}
          onClick={() => redirect({
            pathname: `/regatta/course/${id}`,
          })}
          outline
          size={'sm'}
        >
          {t('Edit')}
        </Button>
        <DeleteDialog
          content={t('Do you really want to delete?')}
          id={`delete-regatta-course-dialog-${id}`}
          labelCloseButton={t('Close')}
          labelDeleteButton={t('Delete')}
          onAction={() => defaultDeleteItem({
            action: async () => Services.Api.deleteRegattaCourse(dataItem),
          })}
          subTitle={dataItem.Name}
          title={t('Regatta message')}
        >
          <Button
            color={'danger'}
            id={`delete-regatta-course-button-${id}`}
            outline
            size={'sm'}
          >
            {t('Delete')}
          </Button>
        </DeleteDialog>
      </div>
    );
  }

  renderRegattaCourseRaces(regattaCourse: $FullRegattaCourse) {
    const {
      t,
    } = this.props;

    const RegattaCourseId = regattaCourse.id;

    const newItem: $ModelUpsertParams<'RegattaCourseRace'> = {
      EndDate: new Date(),
      Name: '',
      Public: true,
      RegattaCourseId,
      StartDate: new Date(),
      UpadateInterval: 3,
    };

    return (
      <tr>
        <td colSpan={6}>
          <div>
            {regattaCourse.RegattaCourseRaces.map((dataItem: $Model<'RegattaCourseRace'>) => {
              const {
                id,
              } = dataItem;

              const date: null | string = formatTimeRange({
                end: dataItem.EndDate,
                start: dataItem.StartDate,
              });

              return (
                <div
                  className={'d-inline-block mb-1 mr-1'}
                  key={id}
                >
                  <div className={'input-group input-group-sm'}>
                    <div
                      className={'form-control'}
                      style={publicBorder(
                        dataItem.Public,
                        true,
                      )}
                    >
                      {`${dataItem.Name}: `}
                      <span className={'text-bold'}>
                        {date}
                      </span>
                    </div>
                    <FormDialog
                      fields={(
                        <RegattaCourseRaceFields item={dataItem} />
                      )}
                      id={`update-regatta-course-race-dialog-${id}`}
                      labelCloseButton={t('Close')}
                      labelSaveButton={t('Save')}
                      onAction={(values) => defaultUpsertItem({
                        action: async () => {
                          const params = cleanFormValues<$Model<'RegattaCourseRace'>>({
                            ...dataItem,
                            ...values,
                          });

                          await Services.Api.upsertRegattaCourseRace(params);
                        },
                      })}
                      size={'sm'}
                      subTitle={dataItem.Name}
                      title={t('Regatta course race')}
                    >
                      <Button
                        color={'warning'}
                        id={`update-regatta-course-race-button-${id}`}
                        outline
                        size={'sm'}
                      >
                        {t('Edit')}
                      </Button>
                    </FormDialog>
                    <DeleteDialog
                      content={t('Do you really want to delete?')}
                      id={`delete-regatta-course-race-dialog-${id}`}
                      labelCloseButton={t('Close')}
                      labelDeleteButton={t('Delete')}
                      onAction={() => defaultDeleteItem({
                        action: async () => Services.Api.deleteRegattaCourseRace(dataItem),
                      })}
                      subTitle={''}
                      title={t('Regatta course race')}
                    >
                      <Button
                        color={'danger'}
                        id={`delete-regatta-course-race-button-${id}`}
                        outline
                        size={'sm'}
                      >
                        {t('Delete')}
                      </Button>
                    </DeleteDialog>
                  </div>
                </div>
              );
            })}
            <FormDialog
              fields={(
                <RegattaCourseRaceFields
                  item={newItem}
                />
              )}
              id={`create-regatta-course-race-dialog-${RegattaCourseId}`}
              labelCloseButton={t('Close')}
              labelSaveButton={t('Save')}
              onAction={(values) => defaultUpsertItem({
                action: async () => {
                  const params = cleanFormValues<$Model<'RegattaCourseRace'>>({
                    ...newItem,
                    ...values,
                  });

                  await Services.Api.upsertRegattaCourseRace(params);
                },
              })}
              size={'sm'}
              subTitle={t('New')}
              title={t('Regatta course race')}
            >
              <Button
                className={'d-inline-block mb-1'}
                color={'info'}
                id={`create-regatta-course-race-button-${RegattaCourseId}`}
                size={'sm'}
              >
                {t('Add race')}
              </Button>
            </FormDialog>
          </div>
        </td>
      </tr>
    );
  }

  renderTable() {
    const {
      t,
    } = this.props;
    const {
      data,
    } = this.state;

    return data.length === 0 ? (
      <EmptyTable className={'mt-5'} />
    ) : (
      <Table responsive>
        <thead>
          <tr>
            <th>{t('Name')}</th>
            <th>{t('Date created')}</th>
            <ThQty value={data.length} />
          </tr>
        </thead>
        {data.map((dataItem: $FullRegattaCourse) => (
          <React.Fragment key={dataItem.id}>
            <tbody>
              <tr>
                <td
                  className={'border-0 p-1'}
                  colSpan={3}
                  style={{
                    backgroundColor: 'rgba(200,200,200,0.2)',
                  }}
                />
              </tr>
            </tbody>
            <tbody>
              <tr key={dataItem.id}>
                <td>{dataItem.Name}</td>
                <TdDate
                  duration
                  multiple
                  value={dataItem.createdAt}
                />
                <td>
                  {this.renderButtons(dataItem)}
                </td>
              </tr>
              {this.renderRegattaCourseRaces(dataItem)}
            </tbody>
          </React.Fragment>
        ))}
      </Table>
    );
  }

  renderContent() {
    const {
      loading,
    } = this.state;

    return (
      <Card id={adminSections[pageId].list}>
        <Loader
          loading={loading === true}
          overlay
        >
          {this.renderTable()}
        </Loader>
      </Card>
    );
  }

  render() {
    return (
      <Page id={pageId}>
        <>
          {this.renderHeader()}
          {this.renderContent()}
        </>
      </Page>
    );
  }
}

export default RegattaCoursesPageView;
